import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  RewardType,
  selectableRewardTypes,
} from 'src/app/shared/models/constants/reward-type';
import { RewardFormControls } from 'src/app/shared/models/reward-form/reward-form';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { ProductService } from 'src/app/core/api/product/product.service';
import { isEntryLevel } from 'src/app/shared/models/constants/vip-levels';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { catchError, finalize, of, tap } from 'rxjs';
import {
  specialCharValidator,
  vipCheckboxesValidator,
} from 'src/app/shared/validators/registration-validators';
import { requiredValidator } from 'src/app/shared/validators/add-edit-rewards-validators';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { UserService } from 'src/app/core/api/user/user.service';

@Component({
  selector: 'app-reward-details-form',
  templateUrl: './reward-details-form.component.html',
  styleUrls: ['./reward-details-form.component.scss'],
})
/**
 * Component for displaying reward details form
 */
export class RewardDetailsFormComponent implements OnInit {
  @Input() formGroup!: FormGroup<RewardFormControls>;
  @Input() rewardId!: any;
  @Input() availableFamilies!: any[];
  @Input() availableBusinessLevels!: any;

  labels = {
    isRelatedToProduct: 'reward-form.related-To-Product',
    whichProduct: 'reward-form.which-product',
    rewardType: 'reward-form.reward-type',
    rewardFamilies: 'reward-form.reward-families',
    rewardTitle: 'reward-form.reward-title',
    rewardTitleEs: 'reward-form.reward-title-es',
    rewardTitlePt: 'reward-form.reward-title-pt',
    discountValue: 'reward-form.discount-value',
    rewardCost: 'reward-form.reward-cost',
    rewardCode: 'reward-form.reward-code',
    extensionPeriod: 'reward-form.extension-period',
    redemptionLimit: 'reward-form.redemption-limit',
    rewardImage: 'reward-form.reward-image',
    rewardFamilyWarning: 'reward-form.reward-family-warning',
  };
  rewardTypes = selectableRewardTypes;
  extendedProductList: ProductDTO[] = [];
  productList: SelectorData[] = [];
  hasVIPLevelUsersEnabled = false;
  printerSeries?: string[] = [];
  loading = false;
  selectedFamilies = new FormControl<any[] | null>(null);

  /**
   * Constructor for RewardDetailsFormComponent
   */
  constructor(
    private productService: ProductService,
    private userService: UserService,
    private translationService: TranslationService
  ) {}

  /**
   * @description Initializes the component
   */
  ngOnInit(): void {
    this.loading = true;
    this.translationService.translateObject(this.labels);

    this.getAllActiveProducts();
  }

  /**
   * Retrieve all active products
   */
  private getAllActiveProducts(): void {
    this.productService
      .getAllActiveProducts()
      .pipe(
        catchError(() => {
          return of([] as ProductDTO[]);
        }),
        tap((products: ProductDTO[]) => {
          this.extendedProductList = products;
          this.productList = products.map((product) => ({
            label: product.name,
            value: product.id,
          }));

          this.setNewPrinterSelection(
            this.formGroup.get('relatedProduct')?.value?.value
          );
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.getControl('relatedProduct').valueChanges.subscribe(
            (product) => {
              if (product && product.value) {
                this.setNewPrinterSelection(product.value);
              }
            }
          );

          this.getControl('isRelatedToProduct').valueChanges.subscribe(
            (isRelated) => {
              this.updateFieldValidators(isRelated);
              this.getControl('relatedProduct').patchValue(undefined);
              this.getControl('productImage').patchValue('');
              this.getControl('rewardFamilies').patchValue(undefined);
              this.printerSeries = undefined;
            }
          );

          this.getControl('rewardType').valueChanges.subscribe((rewardType) => {
            this.updateExtensionPeriodValidation(rewardType);
          });

          this.updateFieldValidators(
            this.getControl('isRelatedToProduct').value
          );
          this.updateExtensionPeriodValidation(
            this.getControl('rewardType').value
          );

          const selectedValues = this.getControl('rewardFamilies').value ?? [];

          const matchedItems = this.availableFamilies.filter((item) =>
            selectedValues.includes(item.value)
          );
          this.selectedFamilies.patchValue(matchedItems);
          if (!this.formGroup.get('relatedProduct')?.value) {
            this.filterSeries();
          }

          const businessLevels = this.getControl('businessLevels');

          this.hasVIPLevelUsersEnabled = !this.hasAnyEntryLevel(
            businessLevels.value
          );

          if (this.hasVIPLevelUsersEnabled) {
            businessLevels.setValidators(vipCheckboxesValidator(1));
          }

          this.formGroup.updateValueAndValidity();
          this.loading = false;
        },
      });
  }

  /**
   * Return a boolean indicating if the list has any entry level
   * @param {any[]} viplevels
   * @return {boolean} A boolean indicating if the list has any entry level
   */
  private hasAnyEntryLevel(viplevels: any[]): boolean {
    if (viplevels.length === 0) {
      return true;
    }

    for (const vipLevelId of viplevels) {
      if (isEntryLevel(vipLevelId, this.availableBusinessLevels)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Adjusts the form validators based on whether the reward is related to a product.
   * @param {boolean} isRelatedToProduct - Whether the reward is related to a product
   */
  private updateFieldValidators(isRelatedToProduct: boolean): void {
    const nameControls = ['name', 'nameEs', 'namePt'];
    const relatedProductControl = this.getControl('relatedProduct');

    if (isRelatedToProduct) {
      // Remove validations from name fields but keep maxLength
      nameControls.forEach((controlName) => {
        const control = this.getControl(controlName);
        control.clearValidators();
        control.setValidators(Validators.maxLength(200));
        control.updateValueAndValidity({ emitEvent: false });
      });

      // Add required to relatedProduct
      relatedProductControl.setValidators(Validators.required);
      relatedProductControl.updateValueAndValidity({ emitEvent: false });
    } else {
      // Add validations to name fields
      this.setNameFieldsValidators(nameControls);

      // Remove required from relatedProduct
      relatedProductControl.clearValidators();
      relatedProductControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * set Name Fields Validators
   * @param {string[]} nameControls
   */
  private setNameFieldsValidators(nameControls: string[]) {
    const errorTitles = {
      name: 'reward-form.reward-title-validation-en',
      namePt: 'reward-form.reward-title-validation-pt',
      nameEs: 'reward-form.reward-title-validation-es',
    };
    nameControls.forEach((controlName) => {
      const control = this.getControl(controlName);
      control.setValidators([
        requiredValidator(errorTitles[controlName as keyof typeof errorTitles]),
        Validators.maxLength(200),
        specialCharValidator(),
      ]);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Updates the validation for the extension period field based on the reward type.
   * @param {RewardType} rewardType - The selected reward type
   */
  private updateExtensionPeriodValidation(rewardType: any): void {
    const extensionPeriodControl = this.getControl('extensionPeriod');

    if (
      rewardType &&
      rewardType.value === RewardType.CUSTOMER_SERVICE_EXTENSION
    ) {
      extensionPeriodControl.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(3),
        Validators.maxLength(1),
        Validators.pattern(/^[1-9]\d*$/),
      ]);
    } else {
      extensionPeriodControl.clearValidators();
      extensionPeriodControl.reset();
    }

    extensionPeriodControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * @param {SelectorData} data - Selector data to extract name from
   * @return {string} extracted object label
   */
  getObjectLabel(data: SelectorData): string {
    return data.label;
  }

  /**
   * Retrieves a form control from the form group by its name.
   * @param {string} control - The name of the control to retrieve.
   * @return {FormControl} The requested form control.
   */
  getControl(control: string): FormControl {
    return this.formGroup.get(control) as FormControl;
  }

  /**
   * Increments the redemption limit value by 1
   */
  incrementRedemptionLimit(): void {
    const control = this.getControl('redemptionLimit');
    const currentValue = control.value || 0;
    control.setValue(currentValue + 1);
  }

  /**
   * Decrements the redemption limit value by 1, but not below 0
   */
  decrementRedemptionLimit(): void {
    const control = this.getControl('redemptionLimit');
    const currentValue = control.value || 0;
    if (currentValue > 0) {
      control.setValue(currentValue - 1);
    }
  }

  /**
   * Resets the redemption limit input to its initial state
   */
  resetRedemptionLimit(): void {
    const control = this.getControl('redemptionLimit');
    control.reset();
  }

  /**
   * handles the change of a related product
   * @param {any} id the id of the new related product
   */
  handleChangeProduct(id: any) {
    this.setNewPrinterSelection(id);
  }

  /**
   * resolves the correct extensionPeriod suffix
   * @return {string} the correct suffix
   */
  getExtensionPeriodSuffix(): string {
    const extensionPeriodValue = this.getControl('extensionPeriod').value;
    if (extensionPeriodValue > 1) {
      return 'Years';
    } else return 'Year';
  }

  /**
   * sets the printer series based on the selected product
   * @param {any} id
   */
  setNewPrinterSelection(id: any) {
    const newPrinter = this.extendedProductList.find((item) => item.id === id);
    const family = newPrinter?.family.toLocaleUpperCase();

    this.printerSeries = [family!];
  }

  /**
   * sets the printer series based on the selected field
   */
  filterSeries() {
    const selected = this.selectedFamilies.value;
    this.printerSeries = selected?.map((item: any) => item.family);
    this.getControl('rewardFamilies').patchValue(
      selected?.map((item: any) => item.value)
    );
    if (!selected || selected.length === 0) {
      this.hasVIPLevelUsersEnabled = false;
      this.getControl('vipLevels').patchValue([]);
    }
  }
}
